import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion, useAnimation } from "framer-motion";
import {
  AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, Clock, Download, Eye, EyeOff,
  FileArchive, Film, Heart, Image as ImageIcon, Link2, Loader2, Lock, LogOut, MessageCircle, Play, X,
} from "lucide-react";

/*
  RUANG KENANGAN (Berkas Client)
  - Alamat: /berkas atau /berkas?room=KODE (link yang dikirim ke client)
  - Client masuk dengan kode room + password, lalu hanya bisa melihat & mengunduh.
  - Saat ini berjalan dalam MODE DEMO (DEMO = true): kode DEMO-2026, password kenangan.
    Setelah backend siap, ubah DEMO menjadi false.

  Kontrak API yang diharapkan saat DEMO = false:
    POST /api/rooms/:code/unlock   body { password }
         200 -> { token, room }      401/404 -> salah kode/password
         410 -> masa berlaku habis   429 -> terlalu banyak percobaan
    GET  /api/rooms/:code           header Authorization: Bearer <token>   -> room
    room = {
      couple: "Anda & Rizky", date: "2026-09-12", expiresAt: "2027-01-20T00:00:00Z", cover?: url,
      files: [{
        id, type: "photo" | "video" | "zip", name, title?, category, size (byte),
        thumb (kecil), preview (foto ~1600px / video 1080p), url (file penuh, signed URL),
        w, h (foto), duration? ("2:35")
      }]
    }
  Catatan: signed URL untuk unduhan sebaiknya mengirim header Content-Disposition: attachment
  agar browser langsung mengunduh, bukan membuka file di tab baru.
  Pembatasan percobaan password di halaman ini hanya tampilan; batas sebenarnya harus di server.
*/

/* ---------- Konfigurasi ---------- */
const DEMO = true;
const DEMO_CODE = "DEMO-2026";
const DEMO_PASS = "kenangan";
const STUDIO = { name: "DIRGA", whatsapp: "6280000000000" }; // ganti dengan nomor WhatsApp studio
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const SESSION_KEY = "berkas-session";
const MAX_TRIES = 5;
const LOCK_SECONDS = 30;
const WARN_DAYS = 14;
const PAGE = 60; // jumlah foto yang dimuat tiap kali

/* ---------- Helper ---------- */
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const fmtSize = (b = 0) => {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1024))} KB`;
};
const fmtDate = (d) => new Intl.DateTimeFormat("id-ID", { day: "numeric", month: "long", year: "numeric" }).format(new Date(d));
const waLink = (text) => `https://wa.me/${STUDIO.whatsapp}?text=${encodeURIComponent(text)}`;
const readSaved = () => { try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } };

const triggerDownload = (f) => {
  const a = document.createElement("a");
  a.href = f.url; a.download = f.name; a.target = "_blank"; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
};

/* ---------- Data & API ---------- */
// Data contoh supaya halaman bisa dicoba sebelum backend ada.
const demoRoom = () => {
  const cats = ["Akad", "Resepsi", "Prewedding", "Keluarga"];
  const ratios = [[3, 2], [2, 3], [4, 3], [1, 1], [3, 4], [16, 9]];
  const img = (seed, w, h) => `https://picsum.photos/seed/${seed}/${w}/${h}`;
  const files = [];
  for (let i = 1; i <= 48; i++) {
    const [rw, rh] = ratios[(i * 7) % ratios.length];
    const w = 1200, h = Math.round((1200 * rh) / rw);
    const cat = cats[i % cats.length];
    files.push({
      id: `p${i}`, type: "photo", category: cat, name: `Dirga_${cat}_${String(i).padStart(3, "0")}.jpg`,
      size: 2.2e6 + ((i * 37) % 26) * 1e5, w, h,
      thumb: img(`dirga${i}`, 600, Math.round((600 * rh) / rw)),
      preview: img(`dirga${i}`, 1600, Math.round((1600 * rh) / rw)),
      url: img(`dirga${i}`, 2400, Math.round((2400 * rh) / rw)),
    });
  }
  const video = "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4";
  [["Highlight Pernikahan", "Resepsi", 1.2e9], ["Akad Nikah", "Akad", 2.4e9], ["Prewedding Film", "Prewedding", 0.8e9]].forEach(([title, cat, size], i) =>
    files.push({
      id: `v${i}`, type: "video", category: cat, title, name: `${title.replace(/\s+/g, "_")}.mp4`, size,
      thumb: img(`dirga-video${i}`, 900, 506), preview: video, url: video,
    }));
  files.push(
    { id: "z1", type: "zip", category: "Paket", name: "Semua_Foto_Akad.zip", title: "Semua foto Akad", size: 1.1e9, url: null },
    { id: "z2", type: "zip", category: "Paket", name: "Semua_Foto_Resepsi.zip", title: "Semua foto Resepsi", size: 1.6e9, url: null },
  );
  return {
    couple: "Anda & Rizky", date: "2026-09-12",
    expiresAt: new Date(Date.now() + 62 * 864e5).toISOString(),
    cover: img("dirga-cover", 1800, 800), files,
  };
};

async function unlockRoom(code, password) {
  if (DEMO) {
    await sleep(700);
    if (code === DEMO_CODE && password === DEMO_PASS) return { token: "demo", room: demoRoom() };
    throw new Error("INVALID");
  }
  const res = await fetch(`${API}/rooms/${encodeURIComponent(code)}/unlock`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ password }),
  });
  if (res.status === 401 || res.status === 404) throw new Error("INVALID");
  if (res.status === 410) throw new Error("EXPIRED");
  if (res.status === 429) throw new Error("LOCKED");
  if (!res.ok) throw new Error("SERVER");
  return res.json();
}

async function loadRoom(code, token) {
  if (DEMO) {
    if (token === "demo") return demoRoom();
    throw new Error("INVALID");
  }
  const res = await fetch(`${API}/rooms/${encodeURIComponent(code)}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("INVALID");
  return res.json();
}

/* ---------- Komponen kecil ---------- */
function Img({ src, alt = "", className = "", eager = false, style }) {
  const [ok, setOk] = useState(false);
  const ref = useRef(null);
  useEffect(() => { setOk(false); if (ref.current?.complete && ref.current.naturalWidth) setOk(true); }, [src]);
  return (
    <img ref={ref} src={src} alt={alt} loading={eager ? "eager" : "lazy"} draggable={false} style={style}
      onLoad={() => setOk(true)}
      className={`${className} transition-opacity duration-500 ${ok ? "opacity-100" : "opacity-0"}`} />
  );
}

const Tick = ({ on }) => (
  <span className={`flex h-6 w-6 items-center justify-center border ${on ? "border-[#1C1C1C] bg-[#1C1C1C] text-white" : "border-white bg-black/30 text-transparent"}`}>
    <Check className="h-4 w-4" />
  </span>
);

const IconBtn = ({ icon: Icon, label, onClick, active, className = "" }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label}
    className={`flex h-10 w-10 items-center justify-center transition-colors ${className}`}>
    <Icon className={`h-5 w-5 ${active ? "fill-current" : ""}`} />
  </button>
);

/* ---------- Gerbang masuk ---------- */
function Gate({ initialCode, onUnlock }) {
  const [code, setCode] = useState(initialCode);
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [tries, setTries] = useState(0);
  const [lockedUntil, setLockedUntil] = useState(0);
  const [now, setNow] = useState(Date.now());
  const shake = useAnimation();
  const left = Math.max(0, Math.ceil((lockedUntil - now) / 1000));

  useEffect(() => {
    if (!lockedUntil) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [lockedUntil]);
  useEffect(() => {
    if (lockedUntil && left === 0) { setLockedUntil(0); setTries(0); setErr(""); }
  }, [left, lockedUntil]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy || left > 0 || !code.trim() || !pw) return;
    setBusy(true); setErr("");
    try {
      await onUnlock(code.trim().toUpperCase(), pw);
    } catch (ex) {
      if (ex.message === "EXPIRED") return;
      setBusy(false);
      shake.start({ x: [0, -10, 10, -6, 6, 0], transition: { duration: 0.4 } });
      const n = tries + 1;
      setTries(n);
      if (ex.message === "LOCKED" || n >= MAX_TRIES) {
        setLockedUntil(Date.now() + LOCK_SECONDS * 1000); setNow(Date.now());
        setErr("Terlalu banyak percobaan. Tunggu sebentar lalu coba lagi.");
      } else if (ex.message === "SERVER") setErr("Server sedang bermasalah. Coba lagi beberapa saat.");
      else setErr(`Kode atau password tidak cocok. Sisa percobaan: ${MAX_TRIES - n}.`);
    }
  };

  return (
    <div className="grid min-h-screen bg-[#F9F8F6] text-[#1C1C1C] lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-[#E8E3D9] lg:block">
        <Img eager src="https://picsum.photos/seed/dirga-gate/1200/1600" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <p className="font-sans text-xs uppercase tracking-[0.3em]">{STUDIO.name}</p>
          <p className="mt-4 max-w-md font-serif text-5xl font-light leading-tight">Kenangan hari bahagiamu, tersimpan rapi.</p>
        </div>
      </div>

      <div className="flex flex-col px-6 py-8 md:px-12">
        <Link to="/" className="flex w-fit items-center gap-2 font-sans text-sm text-[#5C5C5C] hover:text-[#1C1C1C]">
          <ArrowLeft className="h-4 w-4" /> Beranda
        </Link>

        <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center py-10">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#8A8A8A]">Ruang Kenangan</p>
          <h1 className="mt-3 font-serif text-5xl font-light tracking-tight md:text-6xl">Buka berkasmu</h1>
          <p className="mt-3 font-sans text-sm leading-relaxed text-[#5C5C5C]">
            Masukkan kode ruang dan password yang dikirim oleh tim kami untuk melihat dan mengunduh foto serta videomu.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <label className="block">
              <span className="font-sans text-xs uppercase tracking-wider text-[#5C5C5C]">Kode ruang</span>
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoCapitalize="characters" autoComplete="off" spellCheck={false}
                placeholder="mis. ANDA-RIZKY"
                className="mt-1.5 w-full border border-[#E8E3D9] bg-white px-4 py-3 font-sans text-sm tracking-wider outline-none focus:border-[#1C1C1C]" />
            </label>
            <motion.label animate={shake} className="block">
              <span className="font-sans text-xs uppercase tracking-wider text-[#5C5C5C]">Password</span>
              <span className="relative mt-1.5 block">
                <input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password"
                  className="w-full border border-[#E8E3D9] bg-white py-3 pl-4 pr-12 font-sans text-sm outline-none focus:border-[#1C1C1C]" />
                <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Sembunyikan password" : "Tampilkan password"}
                  className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center text-[#8A8A8A] hover:text-[#1C1C1C]">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </span>
            </motion.label>

            <div className="min-h-[20px] font-sans text-sm text-red-700" aria-live="polite" role="alert">
              {left > 0 ? `${err} (${left} detik)` : err}
            </div>

            <button type="submit" disabled={busy || left > 0 || !code.trim() || !pw}
              className="flex w-full items-center justify-center gap-2 bg-[#1C1C1C] py-3.5 font-sans text-sm uppercase tracking-wider text-white transition-colors hover:bg-black disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {busy ? "Membuka ruang…" : "Masuk ke ruang"}
            </button>
          </form>

          <p className="mt-6 font-sans text-sm text-[#8A8A8A]">
            Lupa password atau belum menerimanya?{" "}
            <a href={waLink("Halo, saya butuh bantuan untuk membuka Ruang Kenangan.")} target="_blank" rel="noreferrer"
              className="text-[#1C1C1C] underline underline-offset-2">Hubungi kami di WhatsApp</a>
          </p>

          {DEMO && (
            <div className="mt-8 border border-dashed border-[#CFC8BA] p-4 font-sans text-xs leading-relaxed text-[#5C5C5C]">
              <p className="font-medium text-[#1C1C1C]">Mode contoh</p>
              <p className="mt-1">Kode <b>{DEMO_CODE}</b> · password <b>{DEMO_PASS}</b></p>
              <button type="button" onClick={() => { setCode(DEMO_CODE); setPw(DEMO_PASS); setErr(""); }}
                className="mt-2 border border-[#1C1C1C] px-3 py-1.5 hover:bg-[#1C1C1C] hover:text-white">Isi otomatis</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Expired({ onBack }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#F9F8F6] px-6 text-center text-[#1C1C1C]">
      <Clock className="h-8 w-8 text-[#B08D57]" />
      <h1 className="mt-5 font-serif text-4xl font-light md:text-5xl">Masa berlaku ruang ini sudah berakhir</h1>
      <p className="mt-4 max-w-md font-sans text-sm leading-relaxed text-[#5C5C5C]">
        Berkas disimpan selama 3 bulan, lalu dihapus otomatis. Kalau kamu masih membutuhkannya, hubungi tim kami, mungkin masih bisa dibantu.
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <a href={waLink("Halo, masa berlaku Ruang Kenangan saya sudah habis. Apakah bisa diperpanjang?")} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#1C1C1C] px-5 py-3 font-sans text-sm text-white hover:bg-black">
          <MessageCircle className="h-4 w-4" /> Hubungi via WhatsApp
        </a>
        <button type="button" onClick={onBack} className="border border-[#E8E3D9] px-5 py-3 font-sans text-sm hover:border-[#1C1C1C]">Coba kode lain</button>
      </div>
    </div>
  );
}

/* ---------- Lightbox foto ---------- */
function Lightbox({ items, index, onIndex, onClose, favs, onFav, onDownload }) {
  const f = items[index];
  const startX = useRef(null);
  const go = useCallback((d) => onIndex((i) => (i + d + items.length) % items.length), [items.length, onIndex]);

  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", key);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = prev; };
  }, [go, onClose]);

  useEffect(() => { // muat foto sebelah lebih dulu supaya terasa cepat
    [index - 1, index + 1].forEach((i) => { const n = items[(i + items.length) % items.length]; if (n) new window.Image().src = n.preview; });
  }, [index, items]);

  if (!f) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 text-white" role="dialog" aria-label="Pratinjau foto">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className="font-sans text-sm">{index + 1} / {items.length}</p>
          <p className="truncate font-sans text-xs text-white/60">{f.name} · {fmtSize(f.size)}</p>
        </div>
        <div className="flex items-center">
          <IconBtn icon={Heart} label={favs.has(f.id) ? "Hapus dari favorit" : "Tandai favorit"} active={favs.has(f.id)} onClick={() => onFav(f.id)} className="hover:bg-white/10" />
          <button type="button" onClick={() => onDownload([f])}
            className="mx-1 inline-flex h-10 items-center gap-2 border border-white/40 px-4 font-sans text-sm hover:bg-white hover:text-black">
            <Download className="h-4 w-4" /> Unduh
          </button>
          <IconBtn icon={X} label="Tutup" onClick={onClose} className="hover:bg-white/10" />
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 md:px-16"
        onClick={(e) => e.target === e.currentTarget && onClose()}
        onPointerDown={(e) => { startX.current = e.clientX; }}
        onPointerUp={(e) => {
          if (startX.current == null) return;
          const dx = e.clientX - startX.current; startX.current = null;
          if (Math.abs(dx) > 60) go(dx > 0 ? -1 : 1);
        }}>
        <Loader2 className="absolute h-6 w-6 animate-spin text-white/40" />
        <Img key={f.id} eager src={f.preview} alt={f.name} className="relative max-h-full max-w-full select-none object-contain" />
        {items.length > 1 && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Foto sebelumnya"
              className="absolute left-2 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center bg-white/10 hover:bg-white/20 md:flex"><ChevronLeft className="h-6 w-6" /></button>
            <button type="button" onClick={() => go(1)} aria-label="Foto berikutnya"
              className="absolute right-2 top-1/2 hidden h-12 w-12 -translate-y-1/2 items-center justify-center bg-white/10 hover:bg-white/20 md:flex"><ChevronRight className="h-6 w-6" /></button>
          </>
        )}
      </div>
      <p className="py-3 text-center font-sans text-xs text-white/50">Geser atau gunakan tombol panah untuk berpindah foto</p>
    </motion.div>
  );
}

function VideoModal({ file, onClose, onDownload }) {
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95 text-white" role="dialog" aria-label="Putar video">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate font-serif text-xl font-light">{file.title || file.name}</p>
          <p className="font-sans text-xs text-white/60">{file.category} · {fmtSize(file.size)} (kualitas penuh)</p>
        </div>
        <div className="flex items-center">
          <button type="button" onClick={() => onDownload([file])}
            className="mx-1 inline-flex h-10 items-center gap-2 border border-white/40 px-4 font-sans text-sm hover:bg-white hover:text-black">
            <Download className="h-4 w-4" /> Unduh video
          </button>
          <IconBtn icon={X} label="Tutup" onClick={onClose} className="hover:bg-white/10" />
        </div>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center px-2 pb-6 md:px-12" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <video src={file.preview} poster={file.thumb} controls autoPlay playsInline className="max-h-full w-full max-w-5xl bg-black" />
      </div>
    </motion.div>
  );
}

/* ---------- Ruang (setelah masuk) ---------- */
function Room({ code, room, onExit }) {
  const favKey = `berkas-fav:${code}`;
  const [tab, setTab] = useState("photo");
  const [cat, setCat] = useState("all");
  const [favOnly, setFavOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [sel, setSel] = useState(() => new Set());
  const [lb, setLb] = useState(null);
  const [video, setVideo] = useState(null);
  const [limit, setLimit] = useState(PAGE);
  const [dl, setDl] = useState(null);
  const [toast, setToast] = useState("");
  const [favs, setFavs] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(favKey) || "[]")); } catch { return new Set(); }
  });
  const sentinel = useRef(null);

  const photos = useMemo(() => room.files.filter((f) => f.type === "photo"), [room]);
  const videos = useMemo(() => room.files.filter((f) => f.type === "video"), [room]);
  const zips = useMemo(() => room.files.filter((f) => f.type === "zip"), [room]);
  const list = tab === "photo" ? photos : videos;
  const cats = useMemo(() => {
    const m = new Map();
    list.forEach((f) => m.set(f.category, (m.get(f.category) || 0) + 1));
    return [...m];
  }, [list]);
  const shown = useMemo(
    () => list.filter((f) => (cat === "all" || f.category === cat) && (tab !== "photo" || !favOnly || favs.has(f.id))),
    [list, cat, favOnly, favs, tab],
  );
  const total = useMemo(() => room.files.filter((f) => f.type !== "zip").reduce((s, f) => s + (f.size || 0), 0), [room]);
  const selected = useMemo(() => room.files.filter((f) => sel.has(f.id)), [room, sel]);
  const selSize = selected.reduce((s, f) => s + (f.size || 0), 0);
  const daysLeft = Math.ceil((new Date(room.expiresAt) - Date.now()) / 864e5);
  const warn = daysLeft <= WARN_DAYS;
  const cover = room.cover || photos[0]?.preview;

  const say = (msg) => { setToast(msg); };
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 4000); return () => clearTimeout(t); }, [toast]);
  useEffect(() => { try { localStorage.setItem(favKey, JSON.stringify([...favs])); } catch { /* abaikan */ } }, [favs, favKey]);
  useEffect(() => { setLimit(PAGE); }, [tab, cat, favOnly]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setLimit((n) => n + PAGE), { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [tab, cat, favOnly, shown.length, limit]);
  useEffect(() => {
    const key = (e) => { if (e.key === "Escape" && selectMode && lb == null && !video) { setSelectMode(false); setSel(new Set()); } };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [selectMode, lb, video]);

  const toggleFav = useCallback((id) => setFavs((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }), []);
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const closeSelect = () => { setSelectMode(false); setSel(new Set()); };
  const switchTab = (t) => { setTab(t); setCat("all"); setFavOnly(false); };

  const downloadFiles = async (files) => {
    const real = files.filter((f) => f.url);
    if (!real.length) { say("Ini data contoh, jadi file aslinya belum tersedia."); return; }
    setDl({ done: 0, total: real.length });
    for (let i = 0; i < real.length; i++) {
      triggerDownload(real[i]);
      setDl({ done: i + 1, total: real.length });
      if (real.length > 1) await sleep(600);
    }
    setDl(null);
    say(real.length === 1 ? "Unduhan dimulai. Cek folder Downloads." : `${real.length} file diunduh. Jika browser meminta izin, pilih Izinkan.`);
  };
  const copyLink = async () => {
    const url = `${window.location.origin}/berkas?room=${encodeURIComponent(code)}`;
    try { await navigator.clipboard.writeText(url); say("Link ruang disalin. Kirim passwordnya secara terpisah ya."); }
    catch { say(url); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1C1C1C]">
      <header className="border-b border-[#E8E3D9] px-6 py-4 md:px-12">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 font-sans text-sm text-[#5C5C5C] hover:text-[#1C1C1C]">
            <ArrowLeft className="h-4 w-4" /> Beranda
          </Link>
          <span className="font-sans text-xs uppercase tracking-[0.3em]">{STUDIO.name}</span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={copyLink} className="inline-flex items-center gap-2 px-3 py-2 font-sans text-sm text-[#5C5C5C] hover:bg-[#EFEBE3] hover:text-[#1C1C1C]">
              <Link2 className="h-4 w-4" /> <span className="hidden sm:inline">Salin link</span>
            </button>
            <button type="button" onClick={onExit} className="inline-flex items-center gap-2 px-3 py-2 font-sans text-sm text-[#5C5C5C] hover:bg-[#EFEBE3] hover:text-[#1C1C1C]">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Keluar</span>
            </button>
          </div>
        </div>
      </header>

      <section className="relative h-[44vh] min-h-[300px] overflow-hidden bg-[#E8E3D9]">
        {cover && <Img eager src={cover} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-8 text-white md:px-12">
          <div className="mx-auto max-w-[1400px]">
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-white/80">Ruang Kenangan</p>
            <h1 className="mt-2 font-serif text-5xl font-light tracking-tight md:text-7xl">{room.couple}</h1>
            <p className="mt-3 font-sans text-sm text-white/85">
              {fmtDate(room.date)} · {photos.length} foto · {videos.length} video · {fmtSize(total)}
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] space-y-6 px-6 pt-6 md:px-12">
        <div className={`flex flex-wrap items-center justify-between gap-3 border px-4 py-3 font-sans text-sm ${warn ? "border-[#B08D57] bg-[#F4EFE6]" : "border-[#E8E3D9] bg-white"}`}>
          <p className="flex items-start gap-2">
            {warn ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#B08D57]" /> : <Clock className="mt-0.5 h-4 w-4 shrink-0 text-[#8A8A8A]" />}
            <span>
              {warn
                ? <>Berkas akan dihapus pada <b>{fmtDate(room.expiresAt)}</b> ({Math.max(daysLeft, 0)} hari lagi). Unduh yang kamu butuhkan sekarang.</>
                : <>Tersedia sampai <b>{fmtDate(room.expiresAt)}</b> · {daysLeft} hari lagi. Setelah itu berkas dihapus otomatis.</>}
            </span>
          </p>
          {warn && (
            <a href={waLink(`Halo, saya ingin memperpanjang masa berlaku Ruang Kenangan ${room.couple}.`)} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border border-[#1C1C1C] px-3 py-1.5 text-xs hover:bg-[#1C1C1C] hover:text-white">
              <MessageCircle className="h-3.5 w-3.5" /> Minta perpanjangan
            </a>
          )}
        </div>

        {zips.length > 0 && (
          <section>
            <h2 className="font-serif text-2xl font-light">Unduh cepat</h2>
            <p className="mt-1 font-sans text-sm text-[#8A8A8A]">Satu klik untuk membawa semuanya. Paling praktis untuk banyak file.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {zips.map((z) => (
                <button key={z.id} type="button" onClick={() => downloadFiles([z])}
                  className="group flex items-center gap-4 border border-[#E8E3D9] bg-white p-4 text-left transition-colors hover:border-[#1C1C1C]">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#F4EFE6]"><FileArchive className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-serif text-xl font-light">{z.title || z.name}</span>
                    <span className="font-sans text-xs text-[#8A8A8A]">ZIP · {fmtSize(z.size)}</span>
                  </span>
                  <Download className="h-5 w-5 text-[#8A8A8A] group-hover:text-[#1C1C1C]" />
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      <div className="sticky top-0 z-30 mt-6 border-y border-[#E8E3D9] bg-[#F9F8F6]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-6 py-3 md:px-12">
          <div className="flex items-center gap-6">
            {[["photo", "Foto", photos.length, ImageIcon], ["video", "Video", videos.length, Film]].map(([id, label, n, Icon]) => (
              <button key={id} type="button" onClick={() => switchTab(id)} disabled={!n}
                className={`flex items-center gap-2 border-b-2 py-1 font-serif text-xl font-light transition-colors disabled:opacity-40 ${tab === id ? "border-[#1C1C1C]" : "border-transparent text-[#8A8A8A] hover:text-[#1C1C1C]"}`}>
                <Icon className="h-4 w-4" /> {label} <span className="font-sans text-xs text-[#8A8A8A]">{n}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 font-sans text-xs">
            {tab === "photo" && favs.size > 0 && (
              <>
                <button type="button" onClick={() => setFavOnly((v) => !v)}
                  className={`inline-flex items-center gap-1.5 border px-3 py-1.5 ${favOnly ? "border-[#1C1C1C] bg-[#1C1C1C] text-white" : "border-[#E8E3D9] bg-white hover:border-[#1C1C1C]"}`}>
                  <Heart className={`h-3.5 w-3.5 ${favOnly ? "fill-current" : ""}`} /> Favorit ({favs.size})
                </button>
                <button type="button" onClick={() => downloadFiles(photos.filter((p) => favs.has(p.id)))}
                  className="inline-flex items-center gap-1.5 border border-[#E8E3D9] bg-white px-3 py-1.5 hover:border-[#1C1C1C]">
                  <Download className="h-3.5 w-3.5" /> Unduh favorit
                </button>
              </>
            )}
            <button type="button" onClick={() => (selectMode ? closeSelect() : setSelectMode(true))}
              className={`inline-flex items-center gap-1.5 border px-3 py-1.5 ${selectMode ? "border-[#1C1C1C] bg-[#1C1C1C] text-white" : "border-[#E8E3D9] bg-white hover:border-[#1C1C1C]"}`}>
              <Check className="h-3.5 w-3.5" /> {selectMode ? "Selesai memilih" : "Pilih"}
            </button>
          </div>
        </div>
        {cats.length > 1 && (
          <div className="mx-auto flex max-w-[1400px] gap-2 overflow-x-auto px-6 pb-3 md:px-12">
            {[["all", list.length], ...cats].map(([c, n]) => (
              <button key={c} type="button" onClick={() => setCat(c)}
                className={`shrink-0 px-3 py-1 font-sans text-xs ${cat === c ? "bg-[#1C1C1C] text-white" : "border border-[#E8E3D9] bg-white text-[#5C5C5C] hover:border-[#1C1C1C]"}`}>
                {c === "all" ? "Semua" : c} · {n}
              </button>
            ))}
          </div>
        )}
      </div>

      <main className="mx-auto max-w-[1400px] px-6 pb-36 pt-6 md:px-12">
        {shown.length === 0 ? (
          <div className="py-24 text-center">
            <p className="font-serif text-3xl font-light">{favOnly ? "Belum ada favorit" : "Belum ada berkas di sini"}</p>
            <p className="mt-2 font-sans text-sm text-[#8A8A8A]">{favOnly ? "Ketuk ikon hati pada foto yang kamu suka, nanti muncul di sini." : "Coba pilih kategori lain."}</p>
          </div>
        ) : tab === "photo" ? (
          <>
            <div className="columns-2 gap-3 md:columns-3 xl:columns-4">
              {shown.slice(0, limit).map((f, i) => (
                <div key={f.id} role="button" tabIndex={0} aria-label={f.name}
                  onClick={() => (selectMode ? toggleSel(f.id) : setLb(i))}
                  onKeyDown={(e) => { if (e.key === "Enter") (selectMode ? toggleSel(f.id) : setLb(i)); }}
                  className={`group relative mb-3 cursor-zoom-in break-inside-avoid overflow-hidden bg-[#EFEBE3] outline-none focus-visible:ring-2 focus-visible:ring-[#1C1C1C] ${sel.has(f.id) ? "ring-2 ring-[#1C1C1C] ring-offset-2" : ""}`}
                  style={{ aspectRatio: `${f.w} / ${f.h}` }}>
                  <Img src={f.thumb} alt={f.name} className="h-full w-full object-cover" />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                  {selectMode && <span className="absolute left-2 top-2"><Tick on={sel.has(f.id)} /></span>}
                  <button type="button" aria-label={favs.has(f.id) ? "Hapus dari favorit" : "Tandai favorit"}
                    onClick={(e) => { e.stopPropagation(); toggleFav(f.id); }}
                    className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center bg-white/90 transition-opacity [@media(hover:none)]:opacity-100 ${favs.has(f.id) ? "opacity-100" : "opacity-0 focus:opacity-100 group-hover:opacity-100"}`}>
                    <Heart className={`h-4 w-4 ${favs.has(f.id) ? "fill-[#1C1C1C]" : ""}`} />
                  </button>
                  {!selectMode && (
                    <button type="button" aria-label="Unduh foto" onClick={(e) => { e.stopPropagation(); downloadFiles([f]); }}
                      className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center bg-white/90 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {limit < shown.length && (
              <div ref={sentinel} className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#8A8A8A]" /></div>
            )}
          </>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((f) => (
              <div key={f.id} className={`group border bg-white ${sel.has(f.id) ? "border-[#1C1C1C]" : "border-[#E8E3D9]"}`}>
                <button type="button" onClick={() => (selectMode ? toggleSel(f.id) : setVideo(f))} aria-label={`Putar ${f.title || f.name}`}
                  className="relative block aspect-video w-full overflow-hidden bg-[#1C1C1C]">
                  <Img src={f.thumb} className="h-full w-full object-cover opacity-90" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 transition-transform group-hover:scale-105"><Play className="ml-0.5 h-6 w-6" /></span>
                  </span>
                  {f.duration && <span className="absolute bottom-2 right-2 bg-black/70 px-2 py-0.5 font-sans text-xs text-white">{f.duration}</span>}
                  {selectMode && <span className="absolute left-2 top-2"><Tick on={sel.has(f.id)} /></span>}
                </button>
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="min-w-0">
                    <p className="truncate font-serif text-xl font-light">{f.title || f.name}</p>
                    <p className="font-sans text-xs text-[#8A8A8A]">{f.category} · {fmtSize(f.size)}</p>
                  </div>
                  <button type="button" onClick={() => downloadFiles([f])} aria-label="Unduh video"
                    className="flex h-10 w-10 shrink-0 items-center justify-center border border-[#E8E3D9] hover:border-[#1C1C1C]"><Download className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <footer className="mt-16 border-t border-[#E8E3D9] pt-6 text-center font-sans text-sm text-[#8A8A8A]">
          Ada kendala atau ingin berkas tambahan?{" "}
          <a href={waLink(`Halo, saya ingin bertanya soal Ruang Kenangan ${room.couple}.`)} target="_blank" rel="noreferrer"
            className="text-[#1C1C1C] underline underline-offset-2">Hubungi kami</a>
        </footer>
      </main>

      <AnimatePresence>
        {selectMode && (
          <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
            className="fixed inset-x-0 bottom-4 z-40 mx-auto w-[calc(100%-2rem)] max-w-[680px] border border-[#1C1C1C] bg-white p-3 shadow-xl">
            <div className="flex flex-wrap items-center justify-between gap-3 font-sans text-sm">
              <p>
                {dl ? `Mengunduh ${dl.done} dari ${dl.total}…` : sel.size ? <><b>{sel.size}</b> dipilih · {fmtSize(selSize)}</> : "Ketuk foto atau video untuk memilih"}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setSel(new Set(shown.map((f) => f.id)))} className="px-3 py-2 text-xs text-[#5C5C5C] hover:text-[#1C1C1C]">Pilih semua</button>
                <button type="button" onClick={closeSelect} className="border border-[#E8E3D9] px-3 py-2 text-xs hover:border-[#1C1C1C]">Batal</button>
                <button type="button" disabled={!sel.size || !!dl} onClick={() => downloadFiles(selected)}
                  className="inline-flex items-center gap-2 bg-[#1C1C1C] px-4 py-2 text-xs text-white hover:bg-black disabled:opacity-40">
                  {dl ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Unduh
                </button>
              </div>
            </div>
            {sel.size > 20 && !dl && <p className="mt-2 font-sans text-xs text-[#8A8A8A]">Banyak file? Paket ZIP di bagian &quot;Unduh cepat&quot; biasanya lebih praktis.</p>}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {lb != null && shown[lb] && tab === "photo" && (
          <Lightbox items={shown} index={lb} onIndex={setLb} onClose={() => setLb(null)} favs={favs} onFav={toggleFav} onDownload={downloadFiles} />
        )}
        {video && <VideoModal file={video} onClose={() => setVideo(null)} onDownload={downloadFiles} />}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
            className={`fixed left-1/2 z-[110] -translate-x-1/2 bg-[#1C1C1C] px-5 py-3 font-sans text-sm text-white shadow-lg ${selectMode ? "bottom-28" : "bottom-6"}`} role="status">
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Halaman ---------- */
export default function BerkasClient() {
  const [params] = useSearchParams();
  const initialCode = (params.get("room") || "").toUpperCase();
  const [session, setSession] = useState(null); // { code, token, room }
  const [booting, setBooting] = useState(true);
  const [expired, setExpired] = useState(false);

  // Kalau tab ini sudah pernah masuk, langsung buka lagi tanpa password.
  useEffect(() => {
    const saved = readSaved();
    if (!saved || (initialCode && saved.code !== initialCode)) { setBooting(false); return; }
    let live = true;
    loadRoom(saved.code, saved.token)
      .then((room) => live && setSession({ code: saved.code, token: saved.token, room }))
      .catch(() => sessionStorage.removeItem(SESSION_KEY))
      .finally(() => live && setBooting(false));
    return () => { live = false; };
  }, [initialCode]);

  const handleUnlock = async (code, password) => {
    try {
      const { token, room } = await unlockRoom(code, password);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ code, token }));
      setSession({ code, token, room });
    } catch (e) {
      if (e.message === "EXPIRED") setExpired(true);
      throw e;
    }
  };
  const exit = () => { sessionStorage.removeItem(SESSION_KEY); setSession(null); setExpired(false); };

  if (booting) {
    return <div className="flex min-h-screen items-center justify-center bg-[#F9F8F6]"><Loader2 className="h-6 w-6 animate-spin text-[#8A8A8A]" /></div>;
  }
  if (session) return <Room code={session.code} room={session.room} onExit={exit} />;
  if (expired) return <Expired onBack={() => setExpired(false)} />;
  return <Gate initialCode={initialCode} onUnlock={handleUnlock} />;
}